/**
 * Header コンポーネント
 *
 * グローバルヘッダー。全画面共通のナビゲーションとLanguageToggleを含む。
 *
 */

import { Link } from 'react-router-dom';
import { LanguageToggle } from '@/components/molecules/LanguageToggle';
import { useI18n } from '@/i18n/useI18n';

/**
 * メニュー項目の型
 */
interface MenuItem {
  labelKey: string;
  href: string;
}

/**
 * メニュー項目定義
 */
const menuItems: MenuItem[] = [
  { labelKey: 'navigation.works', href: '/works' },
  { labelKey: 'navigation.events', href: '/events' },
  { labelKey: 'navigation.timeline', href: '/timeline' },
  { labelKey: 'navigation.about', href: '/about' },
];

/**
 * Header コンポーネント
 *
 * 使用例:
 * ```tsx
 * <Header />
 * ```
 */
export function Header() {
  const { t } = useI18n();

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      {/* Skip link for keyboard navigation */}
      <a
        href="#main-content"
        className="sr-only focus-visible:not-sr-only focus-visible:absolute focus-visible:left-4 focus-visible:top-2 focus-visible:z-50 focus-visible:rounded-md focus-visible:bg-primary focus-visible:px-4 focus-visible:py-2 focus-visible:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        {t('accessibility.skipToMainContent')}
      </a>
      <div className="container mx-auto flex h-14 items-center px-4">
        {/* ロゴ */}
        <Link to="/" className="mr-6 flex items-center space-x-2">
          <span className="text-xl font-bold">{t('site.title')}</span>
        </Link>

        {/* デスクトップナビゲーション */}
        <nav className="hidden md:flex md:flex-1 md:items-center md:justify-between">
          <ul className="flex items-center gap-6">
            {menuItems.map((item) => (
              <li key={item.href}>
                <Link
                  to={item.href}
                  className="text-sm font-medium text-foreground/80 transition-colors hover:text-foreground"
                >
                  {t(item.labelKey)}
                </Link>
              </li>
            ))}
          </ul>

          {/* 言語切替ボタン */}
          <LanguageToggle />
        </nav>

        {/* モバイルメニュー（簡易版） */}
        <div className="flex flex-1 items-center justify-end gap-2 md:hidden">
          <LanguageToggle />
        </div>
      </div>
    </header>
  );
}
